import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

const MOBILE_VERIFICATION_CREATE_MUTATION = gql`
  mutation createMobileVerification($input: MobileVerificationCreateInput!) {
    createMobileVerification(input: $input) {
      success
      errors {
        message
      }
    }
  }
`
const MOBILE_VERIFICATION_UPDATE_MUTATION = gql`
  mutation updateMobileVerification($input: MobileVerificationUpdateInput!) {
    updateMobileVerification(input: $input) {
      success
      message
      user {
        id
        name
        firstName
        lastName
        phoneNumber
        email
      }
    }
  }
`

export interface User {
  id: string
  name: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
}

export interface MobileVerificationCreateInputType {
  phoneNumber: string
  /** Invisible reCAPTCHA v3 token; the backend rejects the send without a
   *  valid one (GSE-12291). Minted with action 'mobile_verification'. */
  recaptchaToken?: string
}

interface MobileVerificationCreatePayload {
  success: boolean
  errors: { message: string }[]
}

interface MobileVerificationCreateMutation {
  createMobileVerification: MobileVerificationCreatePayload
}

export interface MobileVerificationUpdateInputType {
  phoneNumber: string
  guestUserId?: string | null
  /** Order being verified against. Lets the backend attribute the OTP to the
   *  order's user when no session and no user-id param are available — the
   *  arrival that previously failed every correct code (GSE-12002). */
  orderId?: string | null
  verificationCode: string
  /** Nullable on the backend — omit rather than send false, so a verify
   *  doesn't wipe an opt-in recorded elsewhere. */
  smsTrackingOptin?: boolean
}

export interface MobileVerificationUpdatePayload {
  success: boolean
  message: string
  user: User | null
}

interface MobileVerificationUpdateMutation {
  updateMobileVerification: MobileVerificationUpdatePayload
}

export const useMobileVerificationCreateApi = (): UseMutationResult<
  MobileVerificationCreatePayload,
  Error,
  MobileVerificationCreateInputType,
  unknown
> => {
  const client = useGraphQLClient()

  const createMobileVerificationApi = async (
    input: MobileVerificationCreateInputType,
  ): Promise<MobileVerificationCreatePayload> => {
    const result = await client.request<MobileVerificationCreateMutation>(
      MOBILE_VERIFICATION_CREATE_MUTATION,
      { input },
    )
    if (!result?.createMobileVerification) {
      throw new Error('No results returned from API')
    }

    return result.createMobileVerification
  }

  return useMutation({
    mutationFn: createMobileVerificationApi,
  })
}

export const useMobileVerificationUpdateApi = (): UseMutationResult<
  MobileVerificationUpdatePayload,
  Error,
  MobileVerificationUpdateInputType,
  unknown
> => {
  const client = useGraphQLClient()

  const updateMobileVerificationApi = async (
    input: MobileVerificationUpdateInputType,
  ): Promise<MobileVerificationUpdatePayload> => {
    const result = await client.request<MobileVerificationUpdateMutation>(
      MOBILE_VERIFICATION_UPDATE_MUTATION,
      { input },
    )
    if (!result?.updateMobileVerification) {
      throw new Error('No results returned from API')
    }

    return result.updateMobileVerification
  }

  return useMutation({
    mutationFn: updateMobileVerificationApi,
  })
}
